/**
 * RouteErrorBoundary — React class-based error boundary for the router layer.
 *
 * Wraps the Suspense boundary in Router.jsx to catch:
 *   - Failed dynamic imports (chunk load errors after deploy)
 *   - Render errors in lazy-loaded route components
 *
 * Reports to Sentry and shows a recoverable UI with smart detection
 * of chunk errors (shows "Update Available" instead of a scary crash screen).
 */
import React from 'react';
import * as Sentry from '@sentry/react';
import { themes, DS } from '../designSystem.js';

const T = themes.dark;

function isChunkError(error) {
  const msg = error?.message || '';
  return msg.includes('Failed to fetch dynamically imported module')
    || msg.includes('Loading chunk')
    || msg.includes('Importing a module script failed')
    || msg.includes('error loading dynamically imported module');
}

function ErrorFallback({ error, onReset }) {
  const chunk = isChunkError(error);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: T.bg,
      color: T.t1,
      fontFamily: DS.ff.ui,
      padding: 24,
    }}>
      <div style={{
        maxWidth: 480,
        width: '100%',
        background: T.bgCard,
        border: `1px solid ${T.error}44`,
        borderRadius: DS.radius.lg,
        padding: 32,
        boxShadow: T.el2,
      }}>
        <div style={{
          fontSize: DS.font.xl,
          fontWeight: DS.weight.bold,
          color: chunk ? T.info : T.error,
          marginBottom: DS.space.md,
          letterSpacing: DS.tracking.normal,
        }}>
          {chunk ? 'Update Available' : 'Page Error'}
        </div>
        <div style={{
          fontSize: DS.font.md,
          color: T.t3,
          lineHeight: 1.5,
          marginBottom: DS.space['2xl'],
        }}>
          {chunk
            ? 'A new version of the app is available. Reload to get the latest.'
            : 'This page encountered an error. It has been reported automatically.'
          }
        </div>
        {!chunk && error?.message && (
          <div style={{
            fontSize: DS.font.xs,
            color: T.t4,
            background: T.bg,
            borderRadius: DS.radius.md,
            padding: `${DS.space.md}px ${DS.space.lg}px`,
            marginBottom: DS.space['2xl'],
            maxHeight: 100,
            overflow: 'auto',
            wordBreak: 'break-word',
            fontFamily: DS.ff.mono,
            border: `1px solid ${T.b1}`,
          }}>
            {error.message}
          </div>
        )}
        <div style={{ display: 'flex', gap: DS.space.md }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: `${DS.space.md}px ${DS.space['2xl']}px`,
              fontSize: DS.font.sm,
              fontWeight: DS.weight.semi,
              background: T.ac,
              color: T.bgCard,
              border: 'none',
              borderRadius: DS.radius.sm,
              cursor: 'pointer',
            }}
          >
            {chunk ? 'Reload' : 'Reload Page'}
          </button>
          {!chunk && onReset && (
            <button
              onClick={onReset}
              style={{
                padding: `${DS.space.md}px ${DS.space['2xl']}px`,
                fontSize: DS.font.sm,
                fontWeight: DS.weight.normal,
                background: 'transparent',
                color: T.t3,
                border: `1px solid ${T.b2}`,
                borderRadius: DS.radius.sm,
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          )}
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{
              padding: `${DS.space.md}px ${DS.space['2xl']}px`,
              fontSize: DS.font.sm,
              fontWeight: DS.weight.normal,
              background: 'transparent',
              color: T.t3,
              border: `1px solid ${T.b2}`,
              borderRadius: DS.radius.sm,
              cursor: 'pointer',
            }}
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default class RouteErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[RouteErrorBoundary]', error);
    try {
      Sentry.captureException(error, {
        tags: { context: 'RouteErrorBoundary' },
        extra: {
          componentStack: info?.componentStack,
          url: window.location.href,
        },
      });
    } catch (_) { /* Sentry not initialized */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
